import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Archive, ArrowLeft, FileText } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const DOC_LABELS = { photo: "Photograph", signature: "Signature", pan: "PAN Card", aadhaar: "Aadhaar", bc_csp: "BC/CSP Proof" };

function DocPreview({ appId, doc }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isPdf = doc.content_type === "application/pdf";
  useEffect(() => {
    let url;
    (async () => {
      try {
        const r = await api.get(`/admin/applications/${appId}/documents/${doc.doc_type}`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      } catch {}
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [appId, doc.doc_type]);

  const download = async () => {
    try {
      const r = await api.get(`/admin/applications/${appId}/documents/${doc.doc_type}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || `${doc.doc_type}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid={`doc-${doc.doc_type}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="tech-label">{DOC_LABELS[doc.doc_type] || doc.doc_type}</p>
        <Button size="sm" variant="outline" onClick={download} data-testid={`download-${doc.doc_type}`}><Download size={12} className="mr-1"/>Download</Button>
      </div>
      <div className="aspect-[4/3] bg-zinc-100 rounded flex items-center justify-center overflow-hidden">
        {!blobUrl ? <p className="text-xs text-zinc-400">Loading…</p>
          : isPdf ? <div className="text-center"><FileText size={32} className="text-red-500 mx-auto"/><a href={blobUrl} target="_blank" rel="noreferrer" className="text-xs underline mt-2 block">Open PDF</a></div>
          : <img src={blobUrl} alt={doc.doc_type} className="w-full h-full object-contain"/>}
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 truncate">{doc.original_filename}</p>
    </div>
  );
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [status, setStatus] = useState("pending");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [zipping, setZipping] = useState(false);

  const load = () => api.get(`/admin/applications/${id}`).then((r) => { setApp(r.data); setStatus(r.data.status); setRemarks(r.data.remarks || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveStatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/applications/${id}/status`, { status, remarks });
      toast.success("Status updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const r = await api.get(`/admin/applications/${id}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${app.application_no}-documents.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("ZIP download failed"); }
    finally { setZipping(false); }
  };

  if (!app) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 lg:p-10">
      <Link to="/admin/applications" className="tech-label hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={12}/>Applications</Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="tech-label">Application</p>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="detail-app-no">{app.application_no}</h1>
        </div>
        <Button onClick={downloadZip} disabled={zipping} className="bg-zinc-900 text-white h-11" data-testid="download-zip-btn">
          <Archive size={14} className="mr-2"/>{zipping ? "Preparing…" : "Download All (ZIP)"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <section className="lg:col-span-8 space-y-6">
          <div className="border border-zinc-200 rounded-lg bg-white p-6">
            <p className="tech-label mb-4">Applicant Details</p>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {[
                ["Full Name", app.full_name],
                ["Father's Name", app.father_name],
                ["DOB", app.dob],
                ["Gender", app.gender],
                ["Mobile", app.mobile],
                ["Email", app.email],
                ["State", app.state],
                ["District", app.district],
                ["Pincode", app.pincode],
                ["Education", app.education],
                ["PAN", app.pan],
                ["Aadhaar", app.aadhaar],
                ["Training", app.training_status === "with_training" ? "With Training" : "Without Training"],
                ["Fee", `₹${app.fee_amount?.toLocaleString("en-IN")}`],
                ["Training ID", app.has_training_id ? app.training_id : "N/A"],
                ["BC/CSP", app.is_bc_csp ? "Yes" : "No"],
              ].map(([k, v]) => (
                <div key={k}><p className="tech-label">{k}</p><p className="mt-1 font-medium">{v}</p></div>
              ))}
              <div className="sm:col-span-2"><p className="tech-label">Address</p><p className="mt-1">{app.address}</p></div>
            </div>
          </div>

          <div className="border border-zinc-200 rounded-lg bg-white p-6">
            <p className="tech-label mb-4">Status Management</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="tech-label mb-2">New Status</p>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-11" data-testid="status-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="tech-label mb-2">Admin Remarks</p>
                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="remarks-textarea" placeholder="Notes / reason"/>
              </div>
            </div>
            <Button onClick={saveStatus} disabled={busy} className="mt-4 bg-zinc-900 text-white h-11" data-testid="save-status-btn">{busy ? "Saving…" : "Save Status"}</Button>
          </div>

          <div className="border border-zinc-200 rounded-lg bg-white p-6">
            <p className="tech-label mb-4">Status History</p>
            <ol className="relative border-l-2 border-zinc-200 ml-2 space-y-4">
              {(app.status_history || []).map((h, i) => (
                <li key={i} className="ml-4">
                  <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-zinc-900"/>
                  <p className="text-sm font-semibold">{h.status.replace("_", " ")}</p>
                  {h.remarks && <p className="text-xs text-zinc-600 mt-0.5">{h.remarks}</p>}
                  <p className="text-[10px] text-zinc-400 mt-1">{h.changed_by} · {new Date(h.changed_at).toLocaleString("en-IN")}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <aside className="lg:col-span-4">
          <p className="tech-label mb-3">Documents</p>
          <div className="space-y-4">
            {(app.documents_summary || []).map((d) => (
              <DocPreview key={d.doc_type} appId={id} doc={d}/>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
